export type DeviceTheme = 'light' | 'dark' | 'system';

export type PoplachPreset = 'standard' | 'technika' | 'technika-ucast' | 'custom' | 'vyjezd-2026';
export type MapMode = 'auto' | 'fixed' | 'cycle';
export type MapaLayout = 'misto' | 'trasa' | 'misto-trasa' | 'off';
export type DisplejStylOdpovedi = 'STRIPE' | 'BACKGROUND_TINTED';

export interface MapConfig {
    mode: MapMode;
    fixedZoom?: number;
    zoom1?: number;
    zoom2?: number;
    interval?: number;
}

export interface UcastConfig {
    semafor?: boolean;       // default true
    role?: boolean;          // default true
    specializace?: boolean;  // default true
    responzivni?: boolean;   // default true
}

export interface PravidlaVyjezdu {
    enabled: boolean;
    minPocet: number;            // default 4
    vyzadovatVelitele: boolean;  // default true (jen když enabled)
    vyzadovatStrojnika: boolean; // default true (jen když enabled)
}

export interface PoplachConfig {
    preset: PoplachPreset;
    sections: {
        odpovedi: boolean;
        technika: boolean;
        mapa: boolean;
    };
    casVyhlaseni?: boolean;       // default true — odpočet
    datumCasVyhlaseni?: boolean;  // default false — datum a čas vyhlášení
    zobrazitSpecializace?: boolean; // default true — specializace u odpovědí
    zobrazitFunkce?: boolean;       // default true — funkce (VD/S/H) u odpovědí (jen vyjezd-2026)
    mapa?: MapConfig;
    mapaLayout?: MapaLayout;
    ucast?: UcastConfig;
    displejStylOdpovedi?: DisplejStylOdpovedi;  // default 'STRIPE' — pro preset 'vyjezd-2026' ignorováno (interně hardcoded 'BACKGROUND_TINTED')
    displejAdaptivniLayout?: boolean;           // default false — pro preset 'vyjezd-2026' ignorováno (interně hardcoded true)
    pravidlaVyjezdu?: PravidlaVyjezdu;
    // Noční režim (jen pro vyjezd-2026 preset):
    //   'light' (default) — vždy denní (force light)
    //   'dark' — vždy noční (force dark)
    //   'auto' — denní mód 6:00–20:00, jinak noční
    nocniRezim?: 'auto' | 'light' | 'dark';
    // Migrace v2: true = preset je ručně potvrzený nebo už byl auto-upgradnut na vyjezd-2026.
    // Slouží k jednorázovému auto-upgrade legacy presetů (standard/technika/technika-ucast)
    // v parsePoplachConfig — bez tohoto flagu by uživatel nemohl zvolit zpět starý preset.
    _presetMigratedV2?: boolean;
}

export const DEFAULT_MAP_CONFIG: MapConfig = { mode: 'auto' };

export const PRESETS: Record<PoplachPreset, PoplachConfig> = {
    standard: { preset: 'standard', sections: { odpovedi: true, technika: false, mapa: true }, casVyhlaseni: true, datumCasVyhlaseni: true, zobrazitSpecializace: true, mapa: { mode: 'auto' }, mapaLayout: 'misto', displejStylOdpovedi: 'STRIPE', displejAdaptivniLayout: false },
    technika: { preset: 'technika', sections: { odpovedi: false, technika: true, mapa: true }, casVyhlaseni: true, datumCasVyhlaseni: true, mapa: { mode: 'auto' }, mapaLayout: 'misto', displejStylOdpovedi: 'STRIPE', displejAdaptivniLayout: false },
    'technika-ucast': { preset: 'technika-ucast', sections: { odpovedi: true, technika: true, mapa: true }, casVyhlaseni: true, datumCasVyhlaseni: true, mapa: { mode: 'auto' }, mapaLayout: 'misto', ucast: { semafor: true, role: true, specializace: true, responzivni: true }, displejStylOdpovedi: 'STRIPE', displejAdaptivniLayout: false },
    custom: { preset: 'custom', sections: { odpovedi: true, technika: true, mapa: true }, casVyhlaseni: true, datumCasVyhlaseni: true, mapa: { mode: 'auto' }, mapaLayout: 'misto', displejStylOdpovedi: 'STRIPE', displejAdaptivniLayout: false },
    'vyjezd-2026': {
        preset: 'vyjezd-2026',
        sections: { odpovedi: true, technika: true, mapa: true },
        casVyhlaseni: true,
        datumCasVyhlaseni: false,
        zobrazitSpecializace: true,
        mapa: { mode: 'auto' },
        mapaLayout: 'misto',
        displejStylOdpovedi: 'BACKGROUND_TINTED',
        displejAdaptivniLayout: true,
        pravidlaVyjezdu: { enabled: false, minPocet: 4, vyzadovatVelitele: true, vyzadovatStrojnika: true },
        nocniRezim: 'light',
        _presetMigratedV2: true,
    },
};

export const DEFAULT_POPLACH_CONFIG: PoplachConfig = {
    ...PRESETS['vyjezd-2026'],
};

export const parsePoplachConfig = (json?: string): PoplachConfig => {
    if (!json) return DEFAULT_POPLACH_CONFIG;
    try {
        const config: PoplachConfig = JSON.parse(json);
        // Auto-upgrade: legacy presety (standard/technika/technika-ucast) bez migračního flagu
        // přepíšeme na vyjezd-2026. Uživatelské hodnoty (mapaLayout, mapa, ucast) zachováme;
        // sekce/styl/adaptivní layout/pravidla/noční režim převezmeme z PRESETS['vyjezd-2026'].
        const LEGACY_PRESETS: PoplachPreset[] = ['standard', 'technika', 'technika-ucast'];
        if (!config._presetMigratedV2 && LEGACY_PRESETS.includes(config.preset)) {
            const base = PRESETS['vyjezd-2026'];
            config.preset = 'vyjezd-2026';
            config.sections = { ...base.sections };
            config.displejStylOdpovedi = base.displejStylOdpovedi;
            config.displejAdaptivniLayout = base.displejAdaptivniLayout;
            if (config.pravidlaVyjezdu == null) config.pravidlaVyjezdu = { ...base.pravidlaVyjezdu! };
            if (config.nocniRezim == null) config.nocniRezim = base.nocniRezim;
            config._presetMigratedV2 = true;
        }
        // Zpetna kompatibilita: pokud chybi mapaLayout, odvodit z sections.mapa
        if (!config.mapaLayout) {
            config.mapaLayout = config.sections.mapa !== false ? 'misto' : 'off';
        }
        // Zpetna kompatibilita: pokud chybi ucast pro technika-ucast preset
        if (config.preset === 'technika-ucast' && !config.ucast) {
            config.ucast = { semafor: true, role: true, specializace: true };
        }
        // Zpetna kompatibilita: novy styl zobrazeni odpovedi a adaptivni layout
        // Validuj proti aktualnim hodnotam — drive existoval 'COLUMNS_TINTED' ktery je nyni odstranen.
        const VALID_STYLES: DisplejStylOdpovedi[] = ['STRIPE', 'BACKGROUND_TINTED'];
        if (!config.displejStylOdpovedi || !VALID_STYLES.includes(config.displejStylOdpovedi)) {
            config.displejStylOdpovedi = 'STRIPE';
        }
        if (config.displejAdaptivniLayout == null) {
            config.displejAdaptivniLayout = false;
        }
        return config;
    }
    catch { return DEFAULT_POPLACH_CONFIG; }
};

export interface CreateDeviceTokenRequest {
    deviceName?: string;
    theme?: DeviceTheme;
    tokenType?: string;
}

export interface UpdateDeviceTokenRequest {
    deviceName?: string;
    theme?: DeviceTheme;
    poplachConfig?: string;
}

export interface DeviceTokenResponse {
    id: number;
    token: string;
    deviceName?: string;
    theme?: DeviceTheme;
    poplachConfig?: string;
    createdAt: string;
    lastUsed?: string;
}

export interface DeviceTokenThemeResponse {
    theme: DeviceTheme;
    poplachConfig?: string;
    configVersion?: number;
    idleDisplayMode?: string;  // "TEXT" | "BLACK"
}
